import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Settings, Clock, User, Plus, X, Building2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useLearningCenterShares, CreateShareInput } from "@/hooks/useLearningCenterShares";
import { useLearningCenterShareDialog } from "@/contexts/LearningCenterShareDialogContext";
import { ScheduleConfigPanel } from "@/components/report-shares/ScheduleConfigPanel";
import { useBrandManager } from "@/contexts/BrandProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Recipient {
  name: string;
  email: string;
}

const SHARE_TYPES = [
  { value: 'progress_report', label: 'Progress Report' },
  { value: 'enrollment_report', label: 'Enrollment Report' },
  { value: 'completion_report', label: 'Completion Report' },
];

export function GlobalLearningCenterShareDialog() {
  const { state, closeDialog } = useLearningCenterShareDialog();
  const { isOpen, editingShare, onSuccessCallback } = state;
  const { createShare, updateShare, createSchedule, updateSchedule } = useLearningCenterShares();
  const { currentBrandId } = useBrandManager();
  
  const [name, setName] = useState('');
  const [shareType, setShareType] = useState('progress_report');
  const [recipients, setRecipients] = useState<Recipient[]>([{ name: '', email: '' }]);
  const [usePin, setUsePin] = useState(false);
  const [accessPin, setAccessPin] = useState('');
  const [useExpiry, setUseExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined);
  const [reportConfig, setReportConfig] = useState<Record<string, any>>({});
  const [selectedAcademyId, setSelectedAcademyId] = useState<string>('');
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [activeTab, setActiveTab] = useState('details');

  // Dynamic recipients state
  const [includeCompanyEnrollments, setIncludeCompanyEnrollments] = useState(false);
  const [includeContactEmail, setIncludeContactEmail] = useState(true);
  const [includeExternalEmail, setIncludeExternalEmail] = useState(true);
  const [filterBySelectedCourse, setFilterBySelectedCourse] = useState(false);

  // Schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleType, setScheduleType] = useState('weekly');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleDays, setScheduleDays] = useState<number[]>([1]);
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState(1);
  const [timezone, setTimezone] = useState('Europe/Athens');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');

  // Fetch academies
  const { data: academies } = useQuery({
    queryKey: ['lms-academies', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];
      const { data, error } = await supabase
        .from('os_lms_academies')
        .select('id, name')
        .eq('brand_id', currentBrandId)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentBrandId && isOpen,
  });

  // Fetch courses
  const { data: courses } = useQuery({
    queryKey: ['lms-courses', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];
      const { data, error } = await supabase
        .from('os_lms_courses')
        .select('id, title')
        .eq('brand_id', currentBrandId)
        .order('title');
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentBrandId && isOpen,
  });

  useEffect(() => {
    if (isOpen) {
      setName(editingShare?.name || '');
      setShareType(editingShare?.share_type || 'progress_report');
      
      if (editingShare?.report_config?.recipients) {
        setRecipients(editingShare.report_config.recipients);
      } else {
        setRecipients([{ name: '', email: '' }]);
      }
      
      setSelectedAcademyId(editingShare?.report_config?.academy_id || '');
      setSelectedCourseId(editingShare?.report_config?.course_id || '');
      
      // Load dynamic recipients config
      const dynamicRecipients = editingShare?.report_config?.dynamic_recipients;
      setIncludeCompanyEnrollments(dynamicRecipients?.include_company_enrollments || false);
      setIncludeContactEmail(dynamicRecipients?.email_fields?.includes('contact_email') ?? true);
      setIncludeExternalEmail(dynamicRecipients?.email_fields?.includes('external_email') ?? true);
      setFilterBySelectedCourse(dynamicRecipients?.filter_by_course || false);
      
      setUsePin(!!editingShare?.access_pin);
      setAccessPin(editingShare?.access_pin || '');
      setUseExpiry(!!editingShare?.expires_at);
      setExpiryDate(editingShare?.expires_at ? new Date(editingShare.expires_at) : undefined);
      setReportConfig(editingShare?.report_config || {});
      setActiveTab('details');
      
      setScheduleEnabled(!!editingShare?.schedule?.is_enabled);
      setScheduleType(editingShare?.schedule?.schedule_type || 'weekly');
      setScheduleTime(editingShare?.schedule?.schedule_time || '09:00');
      setScheduleDays(editingShare?.schedule?.schedule_days || [1]);
      setScheduleDayOfMonth(editingShare?.schedule?.schedule_day_of_month || 1);
      setTimezone(editingShare?.schedule?.timezone || 'Europe/Athens');
      setEmailSubject(editingShare?.schedule?.email_subject || '');
      setEmailTemplate(editingShare?.schedule?.email_template || '');
    }
  }, [isOpen, editingShare]);

  const addRecipient = () => {
    setRecipients([...recipients, { name: '', email: '' }]);
  };

  const removeRecipient = (index: number) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const updateRecipient = (index: number, field: 'name' | 'email', value: string) => {
    const updated = [...recipients];
    updated[index] = { ...updated[index], [field]: value };
    setRecipients(updated);
  };

  const saveShareContacts = async (shareId: string, recipientsList: Recipient[]) => {
    if (!currentBrandId) return;
    
    const validRecipientsList = recipientsList.filter(r => r.email.trim());
    if (validRecipientsList.length === 0) return;
    
    try {
      // First, delete existing share contacts for this share
      await supabase
        .from('os_lms_share_contacts')
        .delete()
        .eq('brand_id', currentBrandId);
      
      // Upsert each recipient to os_contacts and get contact_id
      const contactsToInsert = await Promise.all(
        validRecipientsList.map(async (r) => {
          // Parse name into first/last name
          const nameParts = (r.name || '').trim().split(' ');
          const firstName = nameParts[0] || null;
          const lastName = nameParts.slice(1).join(' ') || null;
          
          // Call upsert_os_contact RPC
          const { data: contactId } = await supabase.rpc('upsert_os_contact', {
            p_brand_id: currentBrandId,
            p_email: r.email,
            p_phone: null,
            p_first_name: firstName,
            p_last_name: lastName,
            p_organization: null,
            p_title: null,
            p_user_id: null,
            p_module: ['lms'],
            p_activity: ['share recipient'],
            p_source_table: null,
            p_source_id: null,
          });
          
          return {
            brand_id: currentBrandId,
            name: r.name || null,
            email: r.email,
            contact_id: contactId || null,
          };
        })
      );
      
      // Insert recipients with linked contact_ids
      await supabase
        .from('os_lms_share_contacts')
        .insert(contactsToInsert);
    } catch (error) {
      console.error('Error saving share contacts:', error);
    }
  };

  const handleSubmit = async () => {
    const validRecipients = recipients.filter(r => r.email.trim());
    
    // Build email_fields array based on selections
    const emailFields: string[] = [];
    if (includeContactEmail) emailFields.push('contact_email');
    if (includeExternalEmail) emailFields.push('external_email');
    
    const shareData: CreateShareInput = {
      name,
      share_type: shareType,
      access_pin: usePin ? accessPin : undefined,
      expires_at: useExpiry && expiryDate ? expiryDate.toISOString() : undefined,
      report_config: { 
        ...reportConfig, 
        recipients: validRecipients,
        academy_id: selectedAcademyId || undefined,
        course_id: selectedCourseId || undefined,
        dynamic_recipients: includeCompanyEnrollments ? {
          include_company_enrollments: true,
          email_fields: emailFields as ('contact_email' | 'external_email')[],
          filter_by_course: filterBySelectedCourse,
        } : undefined,
      },
    };

    if (editingShare) {
      await updateShare.mutateAsync({ id: editingShare.id, ...shareData });
      
      // Save recipients to os_lms_share_contacts
      await saveShareContacts(editingShare.id, validRecipients);
      
      if (scheduleEnabled) {
        const scheduleData = {
          schedule_type: scheduleType,
          schedule_time: scheduleTime,
          schedule_days: scheduleDays,
          schedule_day_of_month: scheduleDayOfMonth,
          timezone,
          email_subject: emailSubject,
          email_template: emailTemplate,
          is_enabled: true,
        };

        try {
          if (editingShare.schedule) {
            await updateSchedule.mutateAsync({ id: editingShare.schedule.id, ...scheduleData });
          } else {
            await createSchedule.mutateAsync({ share_id: editingShare.id, ...scheduleData });
          }
        } catch (scheduleError) {
          console.error('Schedule operation failed:', scheduleError);
          // Share was updated successfully, continue even if schedule fails
        }
      }
    } else {
      const newShare = await createShare.mutateAsync(shareData);
      
      // Save recipients to os_lms_share_contacts for new shares too
      if (newShare) {
        await saveShareContacts(newShare.id, validRecipients);
      }
      
      if (scheduleEnabled && newShare) {
        try {
          await createSchedule.mutateAsync({
            share_id: newShare.id,
            schedule_type: scheduleType,
            schedule_time: scheduleTime,
            schedule_days: scheduleDays,
            schedule_day_of_month: scheduleDayOfMonth,
            timezone,
            email_subject: emailSubject,
            email_template: emailTemplate,
            is_enabled: true,
          });
        } catch (scheduleError) {
          console.error('Schedule creation failed:', scheduleError);
          // Share was created successfully, continue even if schedule fails
        }
      }
    }

    onSuccessCallback?.();
    closeDialog();
  };

  const isSubmitting = createShare.isPending || updateShare.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()} modal={true}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug
            popupName={editingShare ? "Edit Learning Center Share" : "Create Learning Center Share"}
            popupContext="/learning-center-shares"
            isOpen={isOpen}
          >
            {editingShare ? 'Edit Learning Center Share' : 'Create Learning Center Share'}
          </DialogTitleWithBug>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Report Config
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Schedule
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Share Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Q1 Course Enrollment Report"
              />
            </div>

            <div className="space-y-2">
              <Label>Share Type *</Label>
              <Select value={shareType} onValueChange={setShareType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {SHARE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Recipients</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addRecipient}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Add Recipient
                </Button>
              </div>
              
              <div className="space-y-2">
                {recipients.map((recipient, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={recipient.name}
                      onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                      placeholder="Name"
                      className="flex-1"
                    />
                    <Input
                      type="email"
                      value={recipient.email}
                      onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1"
                    />
                    {recipients.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRecipient(index)}
                        className="h-9 w-9 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Dynamic Recipients from Company Enrollments */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div className="space-y-0.5">
                    <Label>Include Company Enrollment Contacts</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically include recipients from company enrollments
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={includeCompanyEnrollments} 
                  onCheckedChange={setIncludeCompanyEnrollments} 
                />
              </div>
              
              {includeCompanyEnrollments && (
                <div className="pl-6 space-y-3 border-l-2 border-muted ml-2">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email Fields to Include</Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="contact-email" 
                          checked={includeContactEmail}
                          onCheckedChange={(checked) => setIncludeContactEmail(checked === true)}
                        />
                        <label 
                          htmlFor="contact-email" 
                          className="text-sm cursor-pointer"
                        >
                          Contact Email (primary company contact)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="external-email" 
                          checked={includeExternalEmail}
                          onCheckedChange={(checked) => setIncludeExternalEmail(checked === true)}
                        />
                        <label 
                          htmlFor="external-email" 
                          className="text-sm cursor-pointer"
                        >
                          External Email (if available)
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="filter-by-course" 
                      checked={filterBySelectedCourse}
                      onCheckedChange={(checked) => setFilterBySelectedCourse(checked === true)}
                    />
                    <label 
                      htmlFor="filter-by-course" 
                      className="text-sm cursor-pointer"
                    >
                      Only include enrollments for the selected course (if a course filter is set)
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <Label>PIN Protection</Label>
                <p className="text-sm text-muted-foreground">
                  Require a 6-digit PIN to access the report
                </p>
              </div>
              <Switch checked={usePin} onCheckedChange={setUsePin} />
            </div>

            {usePin && (
              <div className="space-y-2">
                <Label>Access PIN (6 digits)</Label>
                <Input
                  value={accessPin}
                  onChange={(e) => setAccessPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength={6}
                />
              </div>
            )}

            <div className="flex items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <Label>Set Expiry Date</Label>
                <p className="text-sm text-muted-foreground">
                  Link will expire after this date
                </p>
              </div>
              <Switch checked={useExpiry} onCheckedChange={setUseExpiry} />
            </div>

            {useExpiry && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !expiryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {expiryDate ? format(expiryDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={expiryDate}
                    onSelect={setExpiryDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            )}
          </TabsContent>

          <TabsContent value="config" className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label>Filter by Academy</Label>
              <Select value={selectedAcademyId || 'all'} onValueChange={(val) => setSelectedAcademyId(val === 'all' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All academies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All academies</SelectItem>
                  {academies?.map((academy) => (
                    <SelectItem key={academy.id} value={academy.id}>
                      {academy.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Optionally filter the report to show only data for a specific academy
              </p>
            </div>

            <div className="space-y-2">
              <Label>Filter by Course</Label>
              <Select value={selectedCourseId || 'all'} onValueChange={(val) => setSelectedCourseId(val === 'all' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="All courses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All courses</SelectItem>
                  {courses?.map((course) => (
                    <SelectItem key={course.id} value={course.id}>
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Optionally filter the report to show only data for a specific course
              </p>
            </div>
          </TabsContent>

          <TabsContent value="schedule" className="mt-4">
            <ScheduleConfigPanel
              enabled={scheduleEnabled}
              onEnabledChange={setScheduleEnabled}
              scheduleType={scheduleType}
              onScheduleTypeChange={setScheduleType}
              scheduleTime={scheduleTime}
              onScheduleTimeChange={setScheduleTime}
              scheduleDays={scheduleDays}
              onScheduleDaysChange={setScheduleDays}
              scheduleDayOfMonth={scheduleDayOfMonth}
              onScheduleDayOfMonthChange={setScheduleDayOfMonth}
              timezone={timezone}
              onTimezoneChange={setTimezone}
              emailSubject={emailSubject}
              onEmailSubjectChange={setEmailSubject}
              emailTemplate={emailTemplate}
              onEmailTemplateChange={setEmailTemplate}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={closeDialog}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name || !shareType || isSubmitting}>
            {isSubmitting ? 'Saving...' : editingShare ? 'Update Share' : 'Create Share'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
